"use client";

import * as React from "react";
import { ArrowLeft, Clock, MapPin, Phone, ShieldCheck, Wrench } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const highlights = [{
  icon: Clock,
  title: "24/7 Rapid Dispatch",
  desc: "Plumbers on call day and night in Lovejoy"
}, {
  icon: ShieldCheck,
  title: "Licensed & Insured",
  desc: "Complete quality guarantee on all repairs"
}, {
  icon: Wrench,
  title: "Upfront Pricing",
  desc: "Clear quotes before any work begins"
}];
export default function LovejoyClosing() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="lovejoyclosingcta" data-nav="light" className="bg-background py-16 md:py-24 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="relative overflow-hidden rounded-2xl bg-card border border-border p-8 md:p-12 lg:p-16 shadow-lg">
          {/* Subtle decorative background tint */}
          <div className="absolute top-0 right-0 -mr-24 -mt-24 w-96 h-96 rounded-full bg-primary/5 pointer-events-none blur-3xl" />
          <div className="absolute bottom-0 left-0 -ml-24 -mb-24 w-96 h-96 rounded-full bg-accent/10 pointer-events-none blur-3xl" />

          <div className="relative z-10 flex flex-col items-center text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 mb-4">
              <Badge variant="secondary" className="px-3 py-1 text-xs sm:text-sm font-medium">
                <MapPin className="size-3.5 text-primary" />
                Lovejoy, GA Service Area
              </Badge>
            </div>

            <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-card-foreground leading-tight">
              Need a Plumber in Lovejoy? Contact Us Today
            </h2>

            <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
              From sudden pipe bursts and clogged drains to water heater failures, Jonesboro Emergency Plumber delivers dependable, 24/7 solutions to homes and businesses across Lovejoy.
            </p>

            {/* Action Buttons */}
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 w-full sm:w-auto">
              <Button asChild size="lg" className="w-full sm:w-auto h-12 px-8 text-base font-semibold shadow-md">
                <a href="tel:4708479076">
                  <Phone className="size-5 mr-2" />
                  Call (470) 847-9076
                </a>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto h-12 px-8 text-base font-medium">
                <Link to="/contact/">
                  Request Service Online
                </Link>
              </Button>
            </div>

            {/* Hub Link */}
            <div className="mt-6">
              <Link to="/service-areas/" className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                <ArrowLeft className="size-4" />
                Back to all Service Areas
              </Link>
            </div>

            {/* Value Highlights */}
            <div className="mt-12 pt-10 border-t border-border grid grid-cols-1 sm:grid-cols-3 gap-6 w-full text-left">
              {highlights.map((item, i) => {
              const IconComponent = item.icon;
              return <div key={item.title} data-index={i} className="flex items-start gap-3.5 p-3 rounded-lg bg-background/50 border border-border/50">
                    <div className="p-2 rounded-md bg-secondary text-secondary-foreground shrink-0 mt-0.5">
                      <IconComponent className="size-4" />
                    </div>
                    <div>
                      <h3 className="font-default text-sm font-semibold text-foreground">
                        {item.title}
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {item.desc}
                      </p>
                    </div>
                  </div>;
            })}
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}